export const RELEASE_MANIFEST_PATH = "/updates/stable/latest.json" as const;

export type ReleaseChannel = "stable" | "beta";
export type DownloadOperatingSystem = "macos" | "windows";
export type DownloadAvailability = "public" | "beta" | "internal" | "blocked";

export interface TauriUpdatePlatform {
  readonly signature: string;
  readonly url: string;
}

export interface ReleaseDownload {
  readonly id: string;
  readonly os: DownloadOperatingSystem;
  readonly architecture: string;
  readonly label: string;
  readonly availability: DownloadAvailability;
  readonly filename: string;
  readonly url?: string;
  readonly sha256?: string;
  readonly size_bytes?: number;
  readonly system_requirements: readonly string[];
  readonly status_note: string;
}

export interface ReleaseManifest {
  readonly schema_version: 1;
  readonly channel: ReleaseChannel;
  readonly version: string;
  readonly pub_date: string;
  readonly notes: string;
  readonly release_page: string;
  readonly platforms: Readonly<Record<string, TauriUpdatePlatform>>;
  readonly downloads: readonly ReleaseDownload[];
}

const updaterPlatforms = new Set(["darwin-aarch64", "windows-x86_64"]);
const updaterPlatformByDownloadId = new Map([
  ["macos-aarch64", "darwin-aarch64"],
  ["windows-x86_64", "windows-x86_64"]
]);

function canonicalBase64(value: unknown, label: string): string {
  const text = stringValue(value, label);
  if (text.length % 4 !== 0 || !/^[A-Za-z0-9+/]+={0,2}$/u.test(text)) {
    throw new Error(`${label} must be canonical single-line base64`);
  }
  return text;
}

function objectValue(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error(`${label} must be an object`);
  }
  return value as Record<string, unknown>;
}

function stringValue(value: unknown, label: string): string {
  if (typeof value !== "string" || !value.trim()) throw new Error(`${label} must be a non-empty string`);
  return value;
}

function optionalString(value: unknown, label: string): string | undefined {
  return value === undefined ? undefined : stringValue(value, label);
}

function httpsUrl(value: unknown, label: string): string {
  const text = stringValue(value, label);
  const url = new URL(text);
  if (url.protocol !== "https:") throw new Error(`${label} must use https`);
  return text;
}

function parseDownload(value: unknown, index: number): ReleaseDownload {
  const item = objectValue(value, `downloads[${index}]`);
  const availability = stringValue(item.availability, `downloads[${index}].availability`);
  const os = stringValue(item.os, `downloads[${index}].os`);
  if (availability !== "public" && availability !== "beta" && availability !== "internal" && availability !== "blocked") {
    throw new Error(`downloads[${index}].availability is invalid`);
  }
  if (os !== "macos" && os !== "windows") throw new Error(`downloads[${index}].os is invalid`);
  if (!Array.isArray(item.system_requirements) || item.system_requirements.length === 0) {
    throw new Error(`downloads[${index}].system_requirements must be non-empty`);
  }
  const url = optionalString(item.url, `downloads[${index}].url`);
  if ((availability === "public" || availability === "beta") && !url) {
    throw new Error(`downloads[${index}] must have a URL when downloadable`);
  }
  if (url) httpsUrl(url, `downloads[${index}].url`);
  const sha256 = optionalString(item.sha256, `downloads[${index}].sha256`);
  if (sha256 && !/^[a-f0-9]{64}$/u.test(sha256)) throw new Error(`downloads[${index}].sha256 is invalid`);
  const sizeBytes = item.size_bytes;
  if (sizeBytes !== undefined && (!Number.isSafeInteger(sizeBytes) || (sizeBytes as number) <= 0)) {
    throw new Error(`downloads[${index}].size_bytes is invalid`);
  }
  if ((availability === "public" || availability === "beta") && (!sha256 || sizeBytes === undefined)) {
    throw new Error(`downloads[${index}] must have SHA-256 and size when downloadable`);
  }
  return {
    id: stringValue(item.id, `downloads[${index}].id`),
    os,
    architecture: stringValue(item.architecture, `downloads[${index}].architecture`),
    label: stringValue(item.label, `downloads[${index}].label`),
    availability,
    filename: stringValue(item.filename, `downloads[${index}].filename`),
    ...(url ? { url } : {}),
    ...(sha256 ? { sha256 } : {}),
    ...(sizeBytes === undefined ? {} : { size_bytes: sizeBytes as number }),
    system_requirements: item.system_requirements.map((requirement, requirementIndex) =>
      stringValue(requirement, `downloads[${index}].system_requirements[${requirementIndex}]`)
    ),
    status_note: stringValue(item.status_note, `downloads[${index}].status_note`)
  };
}

export function parseReleaseManifest(value: unknown): ReleaseManifest {
  const manifest = objectValue(value, "manifest");
  if (manifest.schema_version !== 1) throw new Error("manifest.schema_version must be 1");
  const channel = stringValue(manifest.channel, "manifest.channel");
  if (channel !== "stable" && channel !== "beta") throw new Error("manifest.channel is invalid");
  const version = stringValue(manifest.version, "manifest.version");
  if (!/^\d+\.\d+\.\d+(?:-[0-9A-Za-z.-]+)?$/u.test(version)) throw new Error("manifest.version is invalid");
  const pubDate = stringValue(manifest.pub_date, "manifest.pub_date");
  if (Number.isNaN(Date.parse(pubDate))) throw new Error("manifest.pub_date is invalid");
  const platformsValue = objectValue(manifest.platforms, "manifest.platforms");
  const platforms: Record<string, TauriUpdatePlatform> = {};
  for (const [platform, platformValue] of Object.entries(platformsValue)) {
    if (!updaterPlatforms.has(platform)) throw new Error(`manifest.platforms.${platform} is unsupported`);
    const item = objectValue(platformValue, `manifest.platforms.${platform}`);
    platforms[platform] = {
      signature: canonicalBase64(item.signature, `manifest.platforms.${platform}.signature`),
      url: httpsUrl(item.url, `manifest.platforms.${platform}.url`)
    };
  }
  if (!Array.isArray(manifest.downloads)) throw new Error("manifest.downloads must be an array");
  const downloads = manifest.downloads.map(parseDownload);
  if (new Set(downloads.map((download) => download.id)).size !== downloads.length) {
    throw new Error("manifest.downloads contains duplicate ids");
  }
  const downloadable = downloads.filter((download) =>
    download.availability === "public" || download.availability === "beta"
  );
  if (downloadable.length > 0) {
    const downloadIds = new Set(downloads.map((download) => download.id));
    const platformIds = new Set(Object.keys(platforms));
    for (const [downloadId, platformId] of updaterPlatformByDownloadId) {
      if (!downloadIds.has(downloadId)) throw new Error(`manifest.downloads must contain ${downloadId}`);
      if (!platformIds.has(platformId)) throw new Error(`manifest.platforms must contain ${platformId}`);
      const download = downloads.find((candidate) => candidate.id === downloadId);
      if (!download || (download.availability !== "public" && download.availability !== "beta")) {
        throw new Error(`manifest.downloads.${downloadId} must be downloadable in a promoted release`);
      }
      for (const [url, label] of [
        [download.url, `download ${downloadId}`],
        [platforms[platformId]?.url, `updater ${platformId}`]
      ] as const) {
        const pathname = new URL(url ?? "https://invalid.invalid/").pathname;
        if (!pathname.startsWith(`/downloads/${version}/`)) {
          throw new Error(`${label} URL must be below /downloads/${version}/`);
        }
      }
      const downloadFilename = decodeURIComponent(new URL(download.url ?? "https://invalid.invalid/").pathname.split("/").at(-1) ?? "");
      if (downloadFilename !== download.filename || !download.filename.includes(version)) {
        throw new Error(`download ${downloadId} filename must match its URL and version ${version}`);
      }
    }
    if (downloadIds.size !== updaterPlatformByDownloadId.size || platformIds.size !== updaterPlatformByDownloadId.size) {
      throw new Error("promoted release must contain exactly the supported downloads and updater platforms");
    }
  }
  const releasePage = httpsUrl(manifest.release_page, "manifest.release_page");
  if (new URL(releasePage).hash !== `#v-${version.replaceAll(".", "-")}`) {
    throw new Error("manifest.release_page must point at the manifest version anchor");
  }
  return {
    schema_version: 1,
    channel,
    version,
    pub_date: pubDate,
    notes: stringValue(manifest.notes, "manifest.notes"),
    release_page: releasePage,
    platforms,
    downloads
  };
}

export function isDownloadable(download: ReleaseDownload): download is ReleaseDownload & { readonly url: string } {
  return (download.availability === "public" || download.availability === "beta") && Boolean(download.url);
}
